import CategoryAPI from "@api/lol/Category.api";
import type IProduct from "@api/models/IProduct";
import avatar from "@assets/avatars/avatar-2.svg";
import AccountDisplay from "@core-components/accountDisplay/AccountDisplay";
import AvatarWithText from "@core-components/avatar-text/AvatarWithText";
import SSBackgroundImage from "@core-components/backgroundImage/BackgroundImage";
import SSBgImageHeadline from "@core-components/backgroundImage/components/BgImageHeadline";
import Emoji from "@core-components/emoji/Emoji";
import NeedHelpBanner from "@core-components/help-banner/NeedHelpBanner";
import Icon from "@core-components/icon/Icon";
import InfoArea from "@core-components/info-area/InfoArea";
import InfoCardGrid from "@core-components/info-card/InfoCardGrid";
import SSProductCard from "@core-components/product/component/SSProductCard";
import LolRegionSelectorBanner from "@core-components/regionSelector/banner/LolRegionSelectorBanner";
import ReviewCarousel from "@core-components/reviews/ReviewCarousel";
import reviews from "@core-components/reviews/defaultReviews";
import SSHeadline from "@core-components/title/SSHeadline";
import SSHeadLineHeader from "@core-components/title/components/SSHeadLineHeader";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import type { AppLolRegion } from "@core-types/region.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { useAppRegion } from "@hooks/region/useAppRegion";
import { Stack, Text } from "@mantine/core";
import LoLFAQ from "@modules/lol/faq/LoLFAQ";
import SEO from "@modules/seo/SEO";
import { useCallback, useEffect, useState } from "react";
import { Trans } from "react-i18next";

const HandlevelPage = () => {
  const { t } = useAppTranslation("pages.handlevel");
  const { t: commonT } = useAppTranslation("common.regions");
  const [products, setProducts] = useState<IProduct[]>([]);
  const { lang } = useAppLanguage();
  const { changeRegion, currentRegion } = useAppRegion();

  const handleChangeRegion = (newRegion: AppLolRegion) => {
    changeRegion(newRegion, loadData);
  };

  const loadData = useCallback(() => {
    CategoryAPI.getHandlevelProducts({ normalized: false }).then(setProducts);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, lang.currentLanguage]);

  return (
    <>
      <SEO
        description={t("seo.description", {
          regionName: commonT(currentRegion),
          regionCode: currentRegion.toUpperCase(),
        })}
        title={t("seo.title", { region: currentRegion.toUpperCase() })}
      />
      <Stack align="center" gap={100} justify="center">
        <SSBackgroundImage>
          <SSBgImageHeadline
            header={<SSHeadLineHeader>{t("banner.header")}</SSHeadLineHeader>}
            subTitle={
              <AvatarWithText
                avatar={avatar}
                text={
                  <Trans i18nKey="pages.handlevel.banner.subTitle">
                    Es-tu à la recherche d&apos;un nouveau compte principal ?
                    Les comptes montés à la main sont le meilleur choix. Nos
                    comptes handlevel ont été nivelés par nos propres équipes et
                    ont un
                    <Text inherit span className="font-bold">
                      {" "}
                      taux de bannissement de 0% !
                    </Text>
                  </Trans>
                }
              />
            }
          >
            <SSWhiteText>{t("banner.titlePrefix")}</SSWhiteText>
            <br />
            <SSLoLGradientText>{t("banner.titleSuffix")}</SSLoLGradientText>
          </SSBgImageHeadline>
        </SSBackgroundImage>
        <InfoCardGrid
          cards={[
            {
              icon: <Icon.Shield />,
              title: t("features.banChance.title"),
              text: t("features.banChance.text"),
            },
            {
              icon: <Icon.Medal />,
              title: t("features.mainAccount.title"),
              text: t("features.mainAccount.text"),
            },
            {
              icon: <Icon.List />,
              title: t("features.recoveryDetails.title"),
              text: t("features.recoveryDetails.text"),
            },
          ]}
        />

        <div className="-mb-24 -ml-8 w-full">
          <LolRegionSelectorBanner onRegionChange={handleChangeRegion} />
        </div>
        <AccountDisplay.List
          emoji="Waving"
          label={
            <Trans i18nKey="list.title" t={t}>
              <SSWhiteText> NOS COMPTE SMURFS </SSWhiteText>
              <SSLoLGradientText>MONTÉS MAINS</SSLoLGradientText>
              <SSWhiteText> ENGLISH LABEL </SSWhiteText>
            </Trans>
          }
        >
          {products.map((product) => (
            <SSProductCard
              key={product.id}
              product={product}
              header={t("list.productCardHeader")}
            />
          ))}
        </AccountDisplay.List>
        <InfoArea
          hasShadow
          className="xs:!p-10"
          description={
            <Trans i18nKey="pages.handlevel.infoArea.description">
              Les comptes handlevel sont très similaires aux comptes
              automatisés, à la différence que ces comptes sont montés
              jusqu&apos;au niveau 30 par des humains et non pas par des bots.
              Ces comptes sont prêts pour les parties classées, avec
              suffisamment de points d&apos;Essence Bleue (EB) pour acheter des
              champions et changer de pseudo.
              <br />
              <br />
              L&apos;avantage de ces comptes est qu&apos;il y a{" "}
              <Text inherit span className="font-bold">
                0% de chance qu&apos;ils se fassent bannir
              </Text>
              , ce qui est parfait si vous souhaitez un compte sur le long terme
              sans être inquiété.
            </Trans>
          }
          fullWidth={false}
          headLine={
            <SSHeadline>
              <Emoji.Pin /> <SSWhiteText>{t("infoArea.headline")}</SSWhiteText>
            </SSHeadline>
          }
        />
        <NeedHelpBanner />
        <InfoCardGrid
          cards={[
            {
              icon: <Icon.FastDelivery />,
              title: t("features.instantDelivery.title"),
              text: t("features.instantDelivery.text"),
              withBackground: false,
            },
            {
              icon: <Icon.Trophy />,
              title: t("features.rankedReady.title"),
              text: t("features.rankedReady.text"),
              withBackground: false,
            },
            {
              icon: <Icon.Chat />,
              title: t("features.premiumSupport.title"),
              text: t("features.premiumSupport.text"),
              withBackground: false,
            },
          ]}
        />
        <LoLFAQ />
        <InfoCardGrid
          cards={[
            {
              icon: <Icon.Shield />,
              title: t("features.lifetimeWarranty.title"),
              text: t("features.lifetimeWarranty.text"),
              withBackground: false,
            },
            {
              icon: <Icon.OpenLock />,
              title: t("features.unverifiedEmail.title"),
              text: t("features.unverifiedEmail.text"),
              withBackground: false,
            },
            {
              icon: <Icon.Basket />,
              title: t("features.bestPrice.title"),
              text: t("features.bestPrice.text"),
              withBackground: false,
            },
          ]}
        />
        <ReviewCarousel className="pb-20" reviews={reviews} />
      </Stack>
    </>
  );
};

export default HandlevelPage;
